import axios, { CancelTokenSource } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getLists, getListsByNextUrl } from './api';
import { List, ListsResponse } from './domain';

type Options = {
  search?: string;
  isActive?: boolean;
  campaignIds?: number[];
  accessFilterId?: number;
  shouldFetch?: boolean;
};

const useLeadList = (
  options: Options = {
    search: undefined,
    isActive: undefined,
    campaignIds: undefined,
    accessFilterId: undefined,
    shouldFetch: true,
  },
) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [list, setList] = useState<List[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const axiosCancelRef = useRef<CancelTokenSource>(axios.CancelToken.source());
  const observer = useRef<IntersectionObserver | undefined>(undefined);
  const search = options.search;
  const isActive = options.isActive;
  const campaignIds = options.campaignIds;
  const accessFilterId = options.accessFilterId;
  const shouldFetch = options.shouldFetch ?? true;

  const getNextPage = useCallback(async () => {
    if (nextUrl !== null) {
      setLoading(true);
      setError(false);

      let resp: ListsResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getListsByNextUrl(nextUrl, axiosCancelRef.current);
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp!.list]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    }
  }, [nextUrl]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(false);

    let resp: ListsResponse | undefined;
    try {
      axiosCancelRef.current = axios.CancelToken.source();
      resp = await getLists(search, isActive, campaignIds, accessFilterId, axiosCancelRef.current);
    } catch (e) {
      setError(true);
      setLoading(false);
      return;
    }

    // Returns undefined if request is canceled
    if (resp === undefined) return;

    setList(resp.list);
    setHasMore(resp.nextPageUrl !== null);
    setNextUrl(resp.nextPageUrl);
    setLoading(false);
  }, [search, isActive, campaignIds, accessFilterId]);

  /** Ref watches for element view intersection and loads more results. Note: Should only be assigned to last element in
   * a list
   * */
  const intersectionObserverRef = useCallback(
    (node: any) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          getNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, getNextPage],
  );

  useEffect(() => {
    setList([]);
  }, [search, isActive, campaignIds, accessFilterId, shouldFetch]);

  useEffect(() => {
    const load = async (search?: string, isActive?: boolean, campaignIds?: number[], accessFilterId?: number) => {
      setLoading(true);
      setError(false);

      let resp: ListsResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getLists(search, isActive, campaignIds, accessFilterId, axiosCancelRef.current);
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp!.list]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    };

    if (shouldFetch) {
      load(search, isActive, campaignIds, accessFilterId);
    }

    return () => {
      // Cancel request if it has already been executed
      axiosCancelRef.current.cancel();
    };
  }, [search, isActive, campaignIds, accessFilterId, shouldFetch]);

  return { loading, error, list, hasMore, reload, intersectionObserverRef };
};

export default useLeadList;
